"use client"

import { useCallback, useRef, useState } from "react"
import { LuShare2 } from "react-icons/lu"

type BrandShareButtonProps = {
  brandName: string
}

const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false
  const mobileMedia = window.matchMedia("(max-width: 768px)")
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent
  return mobileMedia.matches || /android|iphone|ipad|ipod|mobile/i.test(ua)
}

const copyToClipboard = async (value: string): Promise<boolean> => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return true
  }

  if (typeof document === "undefined") return false
  const textarea = document.createElement("textarea")
  textarea.value = value
  textarea.setAttribute("readonly", "")
  textarea.style.position = "absolute"
  textarea.style.left = "-9999px"
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand("copy")
  document.body.removeChild(textarea)
  return copied
}

const BrandShareButton = ({ brandName }: BrandShareButtonProps) => {
  const [feedback, setFeedback] = useState<string | null>(null)
  const feedbackTimer = useRef<number | null>(null)

  const showFeedback = useCallback((message: string) => {
    if (feedbackTimer.current) {
      window.clearTimeout(feedbackTimer.current)
    }
    setFeedback(message)
    feedbackTimer.current = window.setTimeout(() => {
      setFeedback(null)
    }, 2600)
  }, [])

  const onShare = useCallback(async () => {
    if (typeof window === "undefined") return
    const url = window.location.href

    if (
      isMobileDevice() &&
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function"
    ) {
      try {
        await navigator.share({
          title: `${brandName} | Lealti`,
          text: `Mira la tienda de ${brandName} en Lealti`,
          url,
        })
        return
      } catch {
        // Fallback a copiar enlace
      }
    }

    try {
      const copied = await copyToClipboard(url)
      showFeedback(copied ? "Enlace copiado" : "No se pudo copiar el enlace")
    } catch {
      showFeedback("No se pudo copiar el enlace")
    }
  }, [brandName, showFeedback])

  return (
    <div className="relative mt-2">
      <button
        type="button"
        onClick={() => void onShare()}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-lealti-light text-black/40 transition-colors duration-200 hover:text-black"
        aria-label={`Compartir tienda de ${brandName}`}
      >
        <LuShare2 className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
        <span>Compartir</span>
      </button>
      {feedback ? (
        <span className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] -translate-x-1/2 whitespace-nowrap rounded-full border border-black/10 bg-white px-3 py-1 text-[11px] font-lealti-light text-black/60 shadow-sm">
          {feedback}
        </span>
      ) : null}
    </div>
  )
}

export default BrandShareButton
